import { useEffect, useMemo, useState, useCallback } from 'react';
import { getUserData, UserDataResponse } from 'services/userServices';

import { formatDocumentForDisplay } from 'utils/document';

export const useSetValuesProfile = (): any => {
  const [email, setEmail] = useState<string | null>('');
  const [company, setCompany] = useState<string | null>('');
  const [document, setDocument] = useState<string | null>('');
  const [customerCode, setCustomerCode] = useState<string | null>('');
  const [customerSiteCode, setCustomerSiteCode] = useState<string | null>('');
  const [userData, setUserData] = useState<UserDataResponse>();
  const [phoneSF, setPhoneSF] = useState<string>('');
  const [emailSF, setEmailSF] = useState<string>('');
  const [nameSF, setNameSF] = useState<string>('');
  const [contactIdPhoneSF, setContactIdPhoneSF] = useState<string>('');
  const [needUpdate, setNeedUpdate] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const callGetUserData = useCallback(async () => {
    const userDataResponse = await getUserData();
    setUserData(userDataResponse);
  }, []);

  useEffect(() => {
    callGetUserData();
  }, [callGetUserData]);

  const documentClient = useMemo(() => {
    if (!document) return null;
    return formatDocumentForDisplay(document);
  }, [document]);

  const addressBill = useMemo(() => {
    const siteId = localStorage.getItem('@UltragazEmpresarial:customerSiteId');

    const addressReturn = userData?.customerAddresses?.filter(
      (addr) => addr.addressType === 'BILL_TO' && addr.siteId === siteId
    );
    if (addressReturn && addressReturn?.length > 0) return addressReturn[0];
    return null;
  }, [userData]);

  const addressShip = useMemo(() => {
    const siteId = localStorage.getItem('@UltragazEmpresarial:customerSiteId');

    const addressReturn = userData?.customerAddresses?.filter(
      (addr) => addr.addressType === 'SHIP_TO' && addr.siteId === siteId
    );
    if (addressReturn && addressReturn?.length > 0) return addressReturn[0];
    return null;
  }, [userData]);

  const addressLetter = useMemo(() => {
    const siteId = localStorage.getItem('@UltragazEmpresarial:customerSiteId');

    const addressReturn = userData?.customerAddresses?.filter(
      (addr) => addr.addressType === 'SHIP_LETTER_TO' && addr.siteId === siteId
    );
    if (addressReturn && addressReturn?.length > 0) return addressReturn[0];
    return null;
  }, [userData]);

  const contactsResp = (): any => {
    if (!userData) return null;
    const contactIdSf = localStorage.getItem(
      '@UltragazEmpresarial:contactIdSf'
    );
    const siteId = localStorage.getItem('@UltragazEmpresarial:customerSiteId');
    const { customerAddresses } = userData;
    const add = customerAddresses.filter(
      (address) =>
        address.siteId === siteId && address.addressType === 'BILL_TO'
    );

    // "a8602fe0-2647-43a2-ba6f-86b1d0175be6"
    if (add[0]) {
      const { contacts } = add[0];
      const responseContacts = contacts.filter(
        (contact) => contact.contactIdSf === contactIdSf
      );

      if (responseContacts.length > 0) {
        const responseNameSF =
          responseContacts[0]?.name || responseContacts[1]?.name;

        if (responseNameSF) {
          setNameSF(responseNameSF);
        }

        const [typeMail] = responseContacts.filter(
          (respContact) => respContact.contactType === 'EMAIL'
        );

        const [typePhone] = responseContacts.filter(
          (respContact) => respContact.contactType === 'PHONE'
        );

        if (typeMail) {
          const { contact: responseMailSF } = typeMail;
          setEmailSF(responseMailSF);
        }

        if (typePhone) {
          const { contact: responsePhoneSF } = typePhone;
          setPhoneSF(responsePhoneSF);
        }
      }
    }

    return null;
  };

  useEffect(() => {
    contactsResp();
  }, [userData]);

  useEffect(() => {
    if (!userData) return;
    const siteId = localStorage.getItem('@UltragazEmpresarial:customerSiteId');
    const contactIdSf = localStorage.getItem(
      '@UltragazEmpresarial:contactIdSf'
    );
    const { customerAddresses } = userData;
    const add = customerAddresses.filter(
      (address) =>
        address.siteId === siteId && address.addressType === 'SHIP_TO'
    );

    if (add[0]) {
      const { contacts } = add[0];
      const responseContacts = contacts.filter(
        (contact) => contact.contactIdSf === contactIdSf
      );

      if (responseContacts.length > 0) {
        const responseNameSF =
          responseContacts[0]?.name || responseContacts[1]?.name;

        if (responseNameSF) {
          setNameSF(responseNameSF);
        }

        const [typeMail] = responseContacts.filter(
          (respContact) => respContact.contactType === 'EMAIL'
        );

        const [typePhone] = responseContacts.filter(
          (respContact) => respContact.contactType === 'PHONE'
        );

        if (typeMail) {
          const { contact: responseMailSF } = typeMail;
          setEmailSF(responseMailSF);
        }
        if (typePhone) {
          const {
            contact: responsePhoneSF,
            contactId: responseContactIdSFPhone,
            isNeedUpdate: responseNeedUpdate,
          } = typePhone;
          setPhoneSF(responsePhoneSF);
          setContactIdPhoneSF(responseContactIdSFPhone);
          setNeedUpdate(responseNeedUpdate || false);
        }
      }
    }
    setIsLoading(false);
  }, [userData]);

  useEffect(() => {
    const lsEmail = localStorage.getItem('@UltragazEmpresarial:loginEmail');
    const lsCompany = localStorage.getItem('@UltragazEmpresarial:customerName');
    const lsDocument = localStorage.getItem('@UltragazEmpresarial:document');
    const lsCustomerCode = localStorage.getItem(
      '@UltragazEmpresarial:customerCode'
    );
    const lsCustomerSiteCode = localStorage.getItem(
      '@UltragazEmpresarial:customerSiteCode'
    );
    setEmail(lsEmail);
    setCompany(lsCompany);
    setDocument(lsDocument);
    setCustomerCode(lsCustomerCode);
    setCustomerSiteCode(lsCustomerSiteCode);
  }, [setCompany]);

  const revalidate = (): void => {
    callGetUserData();
  };

  return {
    email,
    company,
    documentClient,
    customerCode,
    customerSiteCode,
    addressBill,
    addressShip,
    addressLetter,
    nameSF,
    phoneSF,
    emailSF,
    contactIdPhoneSF,
    siteId: localStorage.getItem('@UltragazEmpresarial:customerSiteId'),
    needUpdate,
    isLoading,
    revalidate,
  };
};
